import {
  type BarcodeDetectorConstructorShape,
  readBarcodeDetectorConstructor,
  type ScannerAdapter,
  type ScannerResult,
} from '@/shared/camera/scanner-adapter';

const preferredFormats = ['qr_code', 'code_128', 'code_39', 'data_matrix', 'ean_13'];

export class BarcodeDetectorScannerAdapter implements ScannerAdapter {
  readonly id = 'barcode-detector';

  constructor(private readonly readConstructor = readBarcodeDetectorConstructor) {}

  async isSupported() {
    const BarcodeDetector = this.readConstructor();

    if (!BarcodeDetector) {
      return false;
    }

    if (!BarcodeDetector.getSupportedFormats) {
      return true;
    }

    const supportedFormats = await BarcodeDetector.getSupportedFormats();

    return preferredFormats.some(format => supportedFormats.includes(format));
  }

  async scan(source: ImageBitmapSource): Promise<ScannerResult[]> {
    const BarcodeDetector = this.readConstructor();

    if (!BarcodeDetector) {
      return [];
    }

    const detector = createDetector(BarcodeDetector);
    const results = await detector.detect(source);

    return results.flatMap(result => {
      if (!result.rawValue) {
        return [];
      }

      return [
        {
          rawValue: result.rawValue,
          format: result.format ?? 'unknown',
        },
      ];
    });
  }
}

function createDetector(BarcodeDetector: BarcodeDetectorConstructorShape) {
  return new BarcodeDetector({
    formats: preferredFormats,
  });
}
